const BUILD_VERSION_META_SELECTOR = 'meta[name="build-version"]';
const RELOAD_ATTEMPT_PREFIX = "__page_open_recovery_attempted_";
const RELOAD_QUERY_PARAM = "__reload";
// Two attempts per build: a single reload can race a rolling deploy and land
// on a container still serving the old build (whose hashed chunks are gone);
// the second attempt lands on the new build. Bounded so a deterministic
// startup error can never reload-loop.
const MAX_RELOAD_ATTEMPTS_PER_BUILD = 2;

// One chunk failure often fires multiple recovery triggers on the same page
// (vite:preloadError + the ErrorBoundary catching the rejected lazy import).
// While the reload navigation is committing, report recovery as started so
// the boundary keeps showing "Refreshing..." instead of the error screen.
let reloadInFlight = false;

const readBuildVersion = (): string => {
  if (typeof document === "undefined") return "server";
  return (
    document
      .querySelector(BUILD_VERSION_META_SELECTOR)
      ?.getAttribute("content") ?? "dev"
  );
};

const safeReason = (reason: string): string =>
  reason.length > 180 ? `${reason.slice(0, 180)}...` : reason;

const parseAttempts = (value: string | null | undefined): number => {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export function attemptClientReloadRecovery(reason: string): boolean {
  if (typeof window === "undefined") return false;
  if (reloadInFlight) return true;

  const buildVersion = readBuildVersion();
  const storageKey = `${RELOAD_ATTEMPT_PREFIX}${buildVersion}`;
  const recoveryReason = safeReason(reason);

  try {
    const currentUrl = new URL(window.location.href);
    const reloadMarker = currentUrl.searchParams.get(RELOAD_QUERY_PARAM);

    // Attempts are tracked both in sessionStorage (survives the reload in the
    // same tab) and in the URL marker (survives when sessionStorage doesn't,
    // e.g. blocked storage). Marker format: <build>:<timestamp>:<attempt>;
    // legacy markers without the attempt segment count as one attempt.
    let attempts = parseAttempts(window.sessionStorage.getItem(storageKey));
    if (reloadMarker?.startsWith(`${buildVersion}:`)) {
      const markerAttempts = parseAttempts(reloadMarker.split(":")[2]) || 1;
      attempts = Math.max(attempts, markerAttempts);
    }

    if (attempts >= MAX_RELOAD_ATTEMPTS_PER_BUILD) {
      console.error(
        "[Page Open Recovery] Reload attempts exhausted for this build:",
        recoveryReason,
      );
      return false;
    }

    const nextAttempt = attempts + 1;
    window.sessionStorage.setItem(storageKey, String(nextAttempt));
    console.warn(
      `[Page Open Recovery] Reloading page (attempt ${nextAttempt}/${MAX_RELOAD_ATTEMPTS_PER_BUILD}) after recoverable app load failure:`,
      recoveryReason,
    );

    currentUrl.searchParams.set(
      RELOAD_QUERY_PARAM,
      `${buildVersion}:${Date.now()}:${nextAttempt}`,
    );
    reloadInFlight = true;
    window.location.replace(currentUrl.toString());
    return true;
  } catch (error) {
    console.error(
      "[Page Open Recovery] Failed to build reload URL, using hard reload:",
      error,
    );
    reloadInFlight = true;
    window.location.reload();
    return true;
  }
}
